import React, { useState, useEffect } from 'react';
import { pushNotificationService } from '../utils/pushNotification';

// 扩展NotificationOptions类型以支持actions
interface ExtendedNotificationOptions extends NotificationOptions {
  actions?: Array<{
    action: string;
    title: string;
    icon?: string;
  }>;
}

interface PushNotificationProps {
  currentTheme: string;
}

const PushNotification: React.FC<PushNotificationProps> = ({ currentTheme }) => {
  const [isSupported, setIsSupported] = useState(false);
  const [permission, setPermission] = useState<NotificationPermission>('default');
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isAutoPushEnabled, setIsAutoPushEnabled] = useState(false);
  const [autoPushInterval, setAutoPushInterval] = useState<NodeJS.Timeout | null>(null);
  const [pushCount, setPushCount] = useState(0);

  useEffect(() => {
    checkSupportAndStatus();
    
    // 清理定时器
    return () => {
      if (autoPushInterval) {
        clearInterval(autoPushInterval);
      }
    };
  }, []);

  const checkSupportAndStatus = async () => {
    const supported = pushNotificationService.isSupported();
    setIsSupported(supported);

    if (supported) {
      setPermission(Notification.permission);
      
      // 确保Service Worker已注册
      try {
        await pushNotificationService.registerServiceWorker();
        const subscription = await pushNotificationService.getCurrentSubscription();
        setIsSubscribed(!!subscription);
      } catch (error) {
        console.error('Service Worker注册失败:', error);
        setError('Service Worker注册失败，请刷新页面重试');
      }
    }
  };

  const handleRequestPermission = async () => {
    setIsLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const newPermission = await pushNotificationService.requestNotificationPermission();
      setPermission(newPermission);
      
      if (newPermission === 'granted') {
        setSuccess('通知权限已授予！');
        // 自动订阅推送
        await handleSubscribe();
      } else if (newPermission === 'denied') {
        setError('通知权限被拒绝，请在浏览器设置中手动开启');
      }
    } catch (error) {
      setError(error instanceof Error ? error.message : '请求权限失败');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubscribe = async () => {
    setIsLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const subscription = await pushNotificationService.subscribeToPush();
      if (subscription) {
        setIsSubscribed(true);
        setSuccess('推送订阅成功！');
        
        // 将订阅信息发送到后端
        await sendSubscriptionToBackend(subscription);
      }
    } catch (error) {
      setError(error instanceof Error ? error.message : '订阅推送失败');
    } finally {
      setIsLoading(false);
    }
  };

  const handleUnsubscribe = async () => {
    setIsLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const result = await pushNotificationService.unsubscribeFromPush();
      if (result) {
        setIsSubscribed(false);
        setSuccess('推送订阅已取消');
      }
    } catch (error) {
      setError(error instanceof Error ? error.message : '取消订阅失败');
    } finally {
      setIsLoading(false);
    }
  };

  const handleTestPush = async () => {
    setIsLoading(true);
    setError(null);
    setSuccess(null);

    try {
      await pushNotificationService.sendTestPush();
      setSuccess('测试推送已发送！');
    } catch (error) {
      setError(error instanceof Error ? error.message : '发送测试推送失败');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLocalNotification = async () => {
    setIsLoading(true);
    setError(null);
    setSuccess(null);

    try {
      await pushNotificationService.showLocalNotification('Delta提醒', {
        body: '这是一条本地测试通知',
        icon: '/delta.png',
        badge: '/delta.png',
        tag: 'test-notification',
        requireInteraction: true,
        actions: [
          {
            action: 'open',
            title: '打开',
            icon: '/delta.png'
          },
          {
            action: 'close',
            title: '关闭',
            icon: '/delta.png'
          }
        ]
      } as ExtendedNotificationOptions);
      setSuccess('本地通知已显示！');
    } catch (error) {
      setError(error instanceof Error ? error.message : '显示本地通知失败');
    } finally {
      setIsLoading(false);
    }
  };

  // 测试Service Worker连接
  const testServiceWorker = async () => {
    setIsLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const isConnected = await pushNotificationService.testServiceWorkerConnection();
      if (isConnected) {
        setSuccess('Service Worker连接正常！');
      } else {
        setError('Service Worker连接失败');
      }
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Service Worker测试失败');
    } finally {
      setIsLoading(false);
    }
  };

  // 通过Service Worker发送本地通知
  const sendServiceWorkerNotification = async () => {
    setIsLoading(true);
    setError(null);
    setSuccess(null);

    try {
      await pushNotificationService.sendLocalNotification('Service Worker通知', {
        body: '这是通过Service Worker发送的通知',
        icon: '/delta.png',
        badge: '/delta.png',
        tag: 'sw-notification',
        requireInteraction: true, // 改为true以支持按钮交互
        actions: [
          {
            action: 'start_timer',
            title: '开始计时',
            icon: '/delta.png'
          },
          {
            action: 'close',
            title: '关闭',
            icon: '/delta.png'
          }
        ],
        data: {
          url: '/',
          timestamp: Date.now(),
          type: 'service-worker'
        }
      } as ExtendedNotificationOptions);
      setSuccess('Service Worker通知已发送！');
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Service Worker通知发送失败');
    } finally {
      setIsLoading(false);
    }
  };

  const sendSubscriptionToBackend = async (subscription: PushSubscription) => {
    try {
      const token = localStorage.getItem('authToken');
      const subscriptionInfo = pushNotificationService.getSubscriptionInfo();
      
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await fetch('/api/push/subscribe', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          subscription: subscriptionInfo,
          userAgent: navigator.userAgent,
          platform: navigator.platform
        })
      });

      if (!response.ok) {
        console.warn('发送订阅信息到后端失败');
      } else {
        console.log('订阅信息已发送到后端');
      }
    } catch (error) {
      console.warn('发送订阅信息到后端时出错:', error);
    }
  };

  // 开始自动SW本地通知
  const startAutoPush = () => {
    if (!isSubscribed) {
      setError('请先订阅推送通知');
      return;
    }

    if (isAutoPushEnabled) {
      setError('自动推送已在运行中');
      return;
    }

    setIsLoading(true);
    setError(null);
    setSuccess(null);

    // 重置推送计数
    setPushCount(0);

    // 立即发送第一次SW本地通知
    sendAutoSWNotification('自动SW本地通知已启动，每10秒发送一次');

    // 设置定时器，每10秒发送一次SW本地通知
    let currentCount = 0;
    const interval = setInterval(async () => {
      currentCount++;
      const now = new Date();
      const timeString = now.toLocaleTimeString('zh-CN', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });

      await sendAutoSWNotification(`第${currentCount}次自动SW本地通知 - ${timeString}`, currentCount);
      
      // 尝试注册后台同步（如果支持）
      if ('serviceWorker' in navigator) {
        try {
          navigator.serviceWorker.ready.then(registration => {
            // 检查是否支持后台同步
            if ('sync' in registration) {
              (registration as any).sync.register('background-notification');
            }
          });
        } catch (error) {
          console.log('后台同步注册失败:', error);
        }
      }
    }, 10000); // 10秒 = 10000毫秒

    setAutoPushInterval(interval);
    setIsAutoPushEnabled(true);
    setSuccess('自动SW本地通知已启动，每10秒发送一次！');
    setIsLoading(false);
  };

  // 发送自动SW本地通知
  const sendAutoSWNotification = async (message: string, count?: number) => {
    try {
      // 增加推送计数
      const currentCount = count || pushCount + 1;
      setPushCount(currentCount);

      // 通过Service Worker发送本地通知
      await pushNotificationService.sendLocalNotification(`Delta自动SW通知 #${currentCount}`, {
        body: message,
        icon: '/delta.png',
        badge: '/delta.png',
        tag: `auto-sw-notification-${currentCount}`,
        requireInteraction: false,
        data: {
          url: '/',
          timestamp: Date.now(),
          type: 'auto-sw-notification',
          pushCount: currentCount
        }
      });

      console.log(`第${currentCount}次自动SW本地通知发送成功`);
    } catch (error) {
      const errorCount = count || pushCount + 1;
      console.error(`第${errorCount}次自动SW本地通知发送失败:`, error);
    }
  };

  // 停止自动SW本地通知
  const stopAutoPush = () => {
    if (autoPushInterval) {
      clearInterval(autoPushInterval);
      setAutoPushInterval(null);
    }
    setIsAutoPushEnabled(false);
    setSuccess(`自动SW本地通知已停止，共发送了${pushCount}次通知`);
  };

  if (!isSupported) {
    return (
      <div className={`push-notification-container theme-${currentTheme}`}>
        <div className="push-notification-card">
          <div className="push-notification-icon">📱</div>
          <h3>推送通知</h3>
          <p className="push-notification-error">
            您的浏览器不支持推送通知功能
          </p>
          <p className="push-notification-hint">
            请使用支持Service Worker的现代浏览器，如Chrome、Firefox、Safari等
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={`push-notification-container theme-${currentTheme}`}>
      <div className="push-notification-card">
        <div className="push-notification-icon">🔔</div>
        <h3>推送通知设置</h3>
        
        {error && (
          <div className="push-notification-error">
            {error}
          </div>
        )}
        
        {success && (
          <div className="push-notification-success">
            {success}
          </div>
        )}

        <div className="push-notification-status">
          <div className="status-item">
            <span className="status-label">浏览器支持:</span>
            <span className="status-value success">✅ 支持</span>
          </div>
          
          <div className="status-item">
            <span className="status-label">通知权限:</span>
            <span className={`status-value ${permission === 'granted' ? 'success' : permission === 'denied' ? 'error' : 'warning'}`}>
              {permission === 'granted' ? '✅ 已授予' : 
               permission === 'denied' ? '❌ 已拒绝' : '⚠️ 未设置'}
            </span>
          </div>
          
          <div className="status-item">
            <span className="status-label">推送订阅:</span>
            <span className={`status-value ${isSubscribed ? 'success' : 'warning'}`}>
              {isSubscribed ? '✅ 已订阅' : '⚠️ 未订阅'}
            </span>
          </div>
          
          {isSubscribed && (
            <div className="status-item">
              <span className="status-label">自动SW通知:</span>
              <span className={`status-value ${isAutoPushEnabled ? 'success' : 'warning'}`}>
                {isAutoPushEnabled ? `🔄 运行中 (${pushCount}次)` : '⏸️ 已停止'}
              </span>
            </div>
          )}
        </div>

        <div className="push-notification-actions">
          {permission !== 'granted' && (
            <button 
              className="push-notification-btn primary"
              onClick={handleRequestPermission}
              disabled={isLoading}
            >
              {isLoading ? '请求中...' : '请求通知权限'}
            </button>
          )}

          {permission === 'granted' && !isSubscribed && (
            <button 
              className="push-notification-btn primary"
              onClick={handleSubscribe}
              disabled={isLoading}
            >
              {isLoading ? '订阅中...' : '订阅推送通知'}
            </button>
          )}

          {isSubscribed && (
            <>
              <button 
                className="push-notification-btn secondary"
                onClick={handleTestPush}
                disabled={isLoading}
              >
                {isLoading ? '发送中...' : '发送测试推送'}
              </button>
              
              <button 
                className="push-notification-btn secondary"
                onClick={handleLocalNotification}
                disabled={isLoading}
              >
                {isLoading ? '显示中...' : '显示本地通知'}
              </button>
              
              <button 
                className="push-notification-btn secondary"
                onClick={testServiceWorker}
                disabled={isLoading}
              >
                {isLoading ? '测试中...' : '测试Service Worker'}
              </button>
              
              <button 
                className="push-notification-btn secondary"
                onClick={sendServiceWorkerNotification}
                disabled={isLoading}
              >
                {isLoading ? '发送中...' : 'SW本地通知'}
              </button>
              
              {!isAutoPushEnabled ? (
                <button 
                  className="push-notification-btn primary"
                  onClick={startAutoPush}
                  disabled={isLoading}
                >
                  {isLoading ? '启动中...' : '启动自动SW通知 (10秒/次)'}
                </button>
              ) : (
                <button 
                  className="push-notification-btn warning"
                  onClick={stopAutoPush}
                  disabled={isLoading}
                >
                  {isLoading ? '停止中...' : `停止自动SW通知 (${pushCount}次)`}
                </button>
              )}
              
              <button 
                className="push-notification-btn danger"
                onClick={handleUnsubscribe}
                disabled={isLoading}
              >
                {isLoading ? '取消中...' : '取消订阅'}
              </button>
            </>
          )}
        </div>

        <div className="push-notification-info">
          <h4>关于推送通知</h4>
          <ul>
            <li>推送通知可以让您在应用关闭时也能收到重要提醒</li>
            <li>在iPhone上，需要将应用添加到主屏幕才能获得最佳体验</li>
            <li>您可以在系统设置中随时管理通知权限</li>
            <li>推送通知仅在HTTPS环境下工作</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default PushNotification;

